import type { WorkspaceDefinition, WorkspaceSurface } from "./workspaceDefinitions.js";
import { Event } from "../../../../base/common/events";
import { IStartup } from "../../../../base/common/lifecycle";
import { createDecorator } from "../../../../platform/instantiation/common/registry";
import { LogLevel } from "../../../../platform/log/common/log";
import { IProperty, IPropertyWithType, Property, PropertyMetadata } from "../../../../platform/properties/common/properties";


export const IUserConfiguration = createDecorator<IUserConfiguration>('userConfiguration');

export interface UserConfigurationChangeEvent {

}

export interface IUserConfiguration extends IStartup {

    readonly onDidChange: Event<UserConfigurationChangeEvent>;

    get canChangeLogLevel(): boolean;
    logLevel: LogLevel;
    get availableLogLevels(): Readonly<Record<LogLevel, string>>;

    theme: string;
    uiLayout: "default" | "minimal";

    hierarchyFilterInactive: boolean;
    richTooltip: boolean;
    /**
     * Auto-generate a short AI default description for each cloud revision on
     * save (revisionDescriptions.ts). Costs a small slice of the team's AI
     * token quota per save; user-written descriptions always win.
     */
    aiRevisionDescriptions: boolean;
    webglPicking: boolean;
    /**
     * Draw the editor's selection chrome in the viewport — the selection frame
     * AND the transform gizmo handles. Separate from {@link webglPicking}:
     * that one is about INPUT (can I click the viewport to select?), this is
     * about OUTPUT, and clicking through to the host page while still seeing
     * your hierarchy selection is the ordinary case. Two owners draw it
     * (ThreeEditor the frame, TransformGizmoService the handles), which is why
     * the preference lives here and not inside either of them.
     */
    selectionDisplay: boolean;
    /**
     * Chrome extension only: let the inspector turn on three's WebGPU timestamp
     * queries when it first sees a renderer. Without it there is no per-pass GPU
     * timing on WebGPU — and it cannot be decided later, three bakes the flag
     * into its cached pass descriptors at the first render.
     */
    gpuTimestamps: boolean;
    /** Transform gizmo axes orientation (world/local). A preference, not session
     *  state — reloads keep it (same reasoning as {@link selectionDisplay}). */
    gizmoSpace: "world" | "local";
    /** Transform gizmo pivot (bounds center / node anchor). */
    gizmoPivot: "center" | "anchor";
    /**
     * Move a light's aim TARGET with the light, so a drag preserves the beam
     * direction instead of swinging it.
     *
     * Default OFF — three's own behaviour is that the target stays put, and that
     * is genuinely what you want when aiming a light at a fixed subject. Note
     * "unlocked" only means the target does not follow: the light's ROTATION is
     * re-derived either way, because that is the only channel glTF saves.
     */
    gizmoLinkLightTarget: boolean;
    /**
     * Chrome extension: deliver whitelisted editor shortcuts (undo/redo, gizmo
     * W/E) even while the HOST page has focus (the editor's keybindings are
     * normally scoped to the editor UI). Default true; the escape hatch for
     * sites whose own keyboard controls collide.
     */
    canvasShortcuts: boolean;
    cameraKeyboardMouseEnabled: boolean;
    cameraKeyboardMouseCapture: boolean;
    cameraGamepadEnabled: boolean;
    cameraGamepadDeadzone: number;
    cameraSpaceMouseEnabled: boolean;
    cameraSmoothing: number;
    cameraSpeed: number;
    cameraRotationSpeedMultiplier: number;
    cameraUseRotationCenter: boolean;
    /** Chrome extension: promote the host canvas into the top layer while flying. */
    cameraFocusCanvasOnFly: boolean;
    /** Chrome extension: resize the promoted canvas and renderer to the viewport while flying. */
    cameraFillCanvasOnFly: boolean;
    /** Experimental: render the main host pass through its own camera using the fly pose. */
    cameraRenderThroughMainCamera: boolean;
    /** Background shown behind a promoted transparent canvas. */
    cameraFlyBackground: CameraFlyBackground;
    cameraTranslationSmoothing: number;
    cameraRotationSmoothing: number;
    propertyAutoRefresh: boolean;
    showSupportButton: boolean;

    setPanelVisible(panel: string, show: boolean): void;
    isPanelVisible(panel: string, fallbackValue?: boolean): boolean;

    setPanelCollapsed(panel: string, collapsed: boolean): void;
    isPanelCollapsed(panel: string, fallbackValue?: boolean): boolean;

    /**
     * Name of the active dock workspace on workspaceSurface. Setting it fires onDidChange so the
     * dock can persist the outgoing layout and restore the new workspace.
     * Stored in the PROFILE scope so it roams with the browser profile.
     */
    activeWorkspace: string;

    /** Current editor location; runtime-only, defaults to embedded. */
    workspaceSurface: WorkspaceSurface;
    /** Read a window's remembered selection without changing the current location. */
    getActiveWorkspace(surface: WorkspaceSurface): string;

    /**
     * Names of all workspaces with a stored layout. Always contains at least
     * the default workspace and the currently active one.
     */
    listWorkspaceLayouts(): string[];

    /**
     * Serialized dock workspace layout for a NAMED workspace (dockview toJSON
     * payload as a JSON string). Read once on layout init/switch; written
     * (debounced) on layout changes. Does NOT fire onDidChange — it is not
     * UI-reactive state. Setting `undefined` deletes the stored layout and
     * removes the name from the workspace index when neither surface has a layout.
     * Popout arrangements are stored independently; omitted surface means embedded.
     */
    getWorkspaceLayout(name: string, surface?: WorkspaceSurface): string | undefined;
    setWorkspaceLayout(name: string, layout: string | undefined, surface?: WorkspaceSurface): void;

    /**
     * Rename a stored workspace layout: moves the layout to the new key and
     * updates the workspace index. If the renamed workspace is the ACTIVE
     * one, `activeWorkspace` is updated through its setter (fires
     * onDidChange so the dock reloads under the new name); otherwise no
     * config change event is fired. The "Default" workspace cannot be
     * renamed (and nothing can be renamed TO "Default").
     */
    renameWorkspaceLayout(oldName: string, newName: string): void;

    /**
     * Delete a stored workspace layout (removes the layout blob and the
     * index entry). If the deleted workspace was active, `activeWorkspace`
     * is set back to "Default" (fires onDidChange). The "Default" workspace
     * cannot be deleted.
     */
    deleteWorkspaceLayout(name: string): void;

    /**
     * Recipe panels already injected into a workspace, ever — the memory that
     * lets the dock tell "never had this panel" from "closed it on purpose".
     * See workspaceReconcile.ts; a version bump clears it with the blob.
     */
    getOfferedPanels(workspace: string, surface?: WorkspaceSurface): string[];
    setOfferedPanels(workspace: string, panelIds: readonly string[], surface?: WorkspaceSurface): void;

    /**
     * Register the BUILT-IN workspaces into the index (plans/
     * workspace-tabs.md). Built-ins are PROTECTED — rename/delete refuse them
     * (Marcel 2026-08-19) — so reseeding every boot is self-healing. VERSION:
     * when a shipped recipe's version is newer than the last seeded one, the
     * user's saved arrangement of that workspace is DROPPED so the new recipe
     * shows (their blob shadows the recipe; unmodified built-ins have no blob
     * and update without this). The first versioned seed only records
     * versions — pre-existing blobs survive the migration, and the dock's
     * ADDITIVE reconcile (workspaceReconcile.ts) is what gets new panels into
     * them without costing the user their arrangement. Bump only when a recipe
     * is genuinely RESTRUCTURED; merely adding a panel needs no bump.
     *
     * Also migrates the legacy un-keyed layout slot into the "Default"
     * workspace, and runs from {@link IStartup.initialize} so the workspace
     * list is seeded before any UI reads it.
     */
    seedWorkspaces(surface?: WorkspaceSurface): void;

    /** Additional built-in recipe for this editor instance only. */
    registerWorkspaceDefinition(definition: WorkspaceDefinition): void;
    /** Replace the workspace catalog for this editor instance. */
    setWorkspaceCatalog(definitions: readonly WorkspaceDefinition[]): void;
    readonly workspaceCatalogIsFixed: boolean;
    getWorkspaceDefinitions(surface?: WorkspaceSurface): readonly WorkspaceDefinition[];
    getWorkspaceDefinition(id: string, surface?: WorkspaceSurface): WorkspaceDefinition | undefined;
    getWorkspaceDisplayName(id: string): string;

    getProperty<T extends keyof IUserConfiguration>(key: T, meta?: PropertyMetadata): IPropertyWithType<IUserConfiguration[T]>;
}


export function configAsProperty<T extends keyof IUserConfiguration>(config: IUserConfiguration, key: T, meta?: PropertyMetadata): IPropertyWithType<IUserConfiguration[T]> {
    const prop = new Property(config, key, typeof config[key], { ...meta, editScope: "editor" });
    return prop as IPropertyWithType<IUserConfiguration[T]>;
}

export type CameraFlyBackground = "auto" | "checker-light" | "checker-dark";

export function isCameraFlyBackgroundTransparent(value: CameraFlyBackground): boolean {
    return value !== "auto";
}
